import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import * as consts from './consts';

function asBool(v: unknown, def = true) {
  return v === true || v === 'true' || v === 1 || v === '1' || (v === undefined ? def : false);
}
function normalizeTransitional(target: { transitional?: any }) {
  const t = target.transitional ?? (target.transitional = {});
  t.clarifyTimeoutError = asBool(t.clarifyTimeoutError, true);
  t.forcedJSONParsing = asBool(t.forcedJSONParsing, true);
  t.silentJSONParsing = asBool(t.silentJSONParsing, true);
  target.transitional = t;
}
normalizeTransitional(axios.defaults as any);

declare module 'axios' {
  interface AxiosRequestConfig<D = any> {
    requiresAuth?: boolean;
  }
  interface InternalAxiosRequestConfig<D = any> {
    requiresAuth?: boolean;
  }
}

function hasHeader(headers: InternalAxiosRequestConfig['headers'] | undefined, key: string) {
  if (!headers) return false;
  const k = key.toLowerCase();
  const h = headers as any;
  if (typeof h.has === 'function') return h.has(key);
  return Object.keys(headers as Record<string, string>).some((x) => x.toLowerCase() === k);
}

function setHeader(
  headers: InternalAxiosRequestConfig['headers'] | undefined,
  key: string,
  value: string,
) {
  if (!headers) return { [key]: value } as any;
  const h = headers as any;
  if (typeof h.set === 'function') {
    h.set(key, value);
    return headers;
  }
  return { ...(headers as Record<string, string>), [key]: value } as any;
}

export type TokenProvider = () => Promise<string | null>;
let tokenProvider: TokenProvider | null = null;
export function setTokenProvider(provider: TokenProvider | null) {
  tokenProvider = provider;
}

export const http: AxiosInstance = axios.create({
  transitional: {
    clarifyTimeoutError: true,
    forcedJSONParsing: true,
    silentJSONParsing: true,
  },
  baseURL: consts.SERVICE_BASEURL,
});
normalizeTransitional(http.defaults as any);

http.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  normalizeTransitional(config as any);

  const method = (config.method ?? 'get').toLowerCase();
  const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;

  // Default Content-Type ONLY when appropriate
  if (method !== 'get' && !isFormData && !hasHeader(config.headers, 'Content-Type')) {
    config.headers = setHeader(config.headers, 'Content-Type', 'application/json');
  }

  // Attach Authorization ONLY if required and not already provided
  if (config.requiresAuth && tokenProvider && !hasHeader(config.headers, 'Authorization')) {
    try {
      const token = await tokenProvider();
      if (token) config.headers = setHeader(config.headers, 'Authorization', `Bearer ${token}`);
    } catch {
      /* ignore */
    }
  }

  return config;
});
